import { useEffect, useRef, useState } from 'react';
import { FieldValues, Path, PathValue, useFormContext } from 'react-hook-form';

import { HorizontalContainer, IconButton, Skeleton } from '@/components';
import { EditIcon, FileRemoveIcon, SearchIcon } from '@/components/Icons';
import { cn } from '@/lib/cn';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import {
    NO_HIGHLIGHTED_OPTION,
    useHighlightedOption,
    useInfiniteScroll,
    useSelectKeyboardHandlers,
    useSelectSearch,
} from '@/caregiverSpace/shared/lib';
import { DropDownOption } from '@/caregiverSpace/shared/model';
import { SpinnerContainer } from '@/caregiverSpace/shared/ui';

import { ExpandToggle } from './ExpandToggle';
import { SearchInputWithIcon } from './SearchInputWithIcon';

type SelectWithSearchProps<TFieldValues extends FieldValues, TOption> = {
    id?: string;
    isLoading: boolean;
    isPending?: boolean;
    onSelect?: (option: DropDownOption<TOption>) => void;
    onRemove?: () => void;
    onSearchChange?: (searchValue: string) => void;
    onReset?: () => void;
    name: Path<TFieldValues>;
    options?: DropDownOption<TOption>[];
    selectedOption?: DropDownOption<TOption> | null;
    fetchNextPage?: () => void;
    isFetching?: boolean;
    hasServerSideSearch?: boolean;
    placeholder?: string;
    removeButtonText?: string;
    className?: string;
    grow?: boolean;
    renderOption?: (option: DropDownOption<TOption>) => React.ReactNode;
};

const SelectWithSearch = <TFieldValues extends FieldValues, TOption>({
    id,
    isLoading,
    isPending,
    onSelect,
    onRemove,
    onSearchChange,
    onReset,
    name,
    options = [],
    selectedOption,
    fetchNextPage,
    isFetching,
    hasServerSideSearch = false,
    placeholder,
    removeButtonText,
    className,
    grow = false,
    renderOption,
}: SelectWithSearchProps<TFieldValues, TOption>) => {
    const { setValue } = useFormContext<TFieldValues>();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [scrollRoot, setScrollRoot] = useState<Element | null>(null);

    const {
        searchTerm,
        setSearchTerm,
        isFocused,
        setIsFocused,
        filteredOptions,
        handleSearchChange,
        currentSearchRef,
        debounceTimeoutRef,
    } = useSelectSearch({
        selectedOption,
        onSearchChange,
        hasServerSideSearch,
        options,
    });

    const setSelectedOption = (option: DropDownOption<TOption>) => {
        setValue(name, option?.id as PathValue<TFieldValues, typeof name>, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    const observerTarget = useInfiniteScroll({
        fetchNextPage: fetchNextPage || (() => {}),
        isFetching: !!isFetching,
        root: scrollRoot,
    });

    const { highlightedOptionIndex, setHighlightedOptionIndex, optionRefs } =
        useHighlightedOption({
            isFocused,
            filteredOptions,
            selectedOption,
            scrollRoot,
        });

    useEffect(() => {
        if (isFocused && dropdownRef.current) {
            const scrollContainer =
                dropdownRef.current.querySelector('.overflow-y-auto');
            if (scrollContainer) {
                setScrollRoot(scrollContainer);
            }
        }
    }, [isFocused]);

    const handleFocus = () => {
        setIsFocused(true);

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        if (selectedOption) {
            setSearchTerm(selectedOption.displayValue);
            currentSearchRef.current = selectedOption.displayValue;

            if (onSearchChange && hasServerSideSearch) {
                const searchCode =
                    (selectedOption.searchValue ??
                        selectedOption.id?.toString()) ||
                    '';

                currentSearchRef.current = searchCode;
                onSearchChange(searchCode);
            }
        }
    };
    const handleBlur = () => {
        setIsFocused(false);
        if (!selectedOption) {
            onReset?.();
        }
    };

    useEffect(() => {
        if (selectedOption && !isFocused) {
            setSearchTerm(selectedOption.displayValue);
            currentSearchRef.current = selectedOption.displayValue;
        }
    }, [selectedOption, isFocused]);

    useEffect(() => {
        currentSearchRef.current = selectedOption?.displayValue ?? '';
    }, [selectedOption?.displayValue]);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const onRemoveButtonClickHandler = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
        e.preventDefault();

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        setSearchTerm('');
        currentSearchRef.current = '';

        setSelectedOption(null);
        onRemove?.();

        setIsFocused(false);
        inputRef.current?.blur();
    };

    const onOptionSelect = (option: DropDownOption<any>) => {
        setSearchTerm(option.displayValue);
        currentSearchRef.current = option.displayValue;

        setSelectedOption(option);

        if (onSelect) {
            onSelect(option);
        }

        setIsFocused(false);
    };

    const { onKeyDownHandler } = useSelectKeyboardHandlers({
        isFocused,
        filteredOptions,
        highlightedOptionIndex,
        setHighlightedOptionIndex,
        setIsFocused,
        inputRef,
        onOptionSelect,
    });

    if (isLoading) {
        return <Skeleton className={cn('h-9', className)} />;
    }

    if (selectedOption && !isFocused) {
        return (
            <HorizontalContainer
                className={cn(
                    'h-9 w-fit items-center justify-between rounded-lg bg-(--color-bg-subtle) p-2 text-sm',
                    className,
                )}
            >
                {selectedOption?.displayNode ?? selectedOption?.displayValue}
                <IconButton
                    onClick={() => {
                        setIsFocused(true);
                        setTimeout(() => {
                            inputRef.current?.focus();
                        }, 0);
                    }}
                    tooltip={R.Edit}
                    icon={<EditIcon variant="action" />}
                />
            </HorizontalContainer>
        );
    }

    return (
        <div className={cn('relative h-9 text-sm', grow && 'grow', className)}>
            <SearchInputWithIcon
                value={searchTerm}
                onChangeValue={handleSearchChange}
                onKeyDown={onKeyDownHandler}
                placeholder={placeholder}
                id={id}
                onFocus={handleFocus}
                onBlur={handleBlur}
                icon={
                    isFocused ? (
                        <ExpandToggle isExpanded={true} />
                    ) : (
                        <SearchIcon />
                    )
                }
                ref={inputRef}
                className={cn('h-9 text-sm', className)}
            />

            {isFocused && (
                <div
                    ref={dropdownRef}
                    className="absolute right-0 left-0 z-10 mt-2 rounded-lg bg-(--color-bg-canvas) shadow-lg"
                >
                    {isPending && options.length === 0 ? (
                        <div className="p-4">
                            <Skeleton className="mb-2 h-6" />
                            <Skeleton className="mb-2 h-6" />
                            <Skeleton className="h-6" />
                        </div>
                    ) : (
                        <>
                            {filteredOptions.length > 0 && (
                                <div className="max-h-[200px] overflow-y-auto">
                                    {filteredOptions.map((option, idx) => {
                                        const isFirst = idx === 0;
                                        const isHighlighted =
                                            idx === highlightedOptionIndex;

                                        return (
                                            <div
                                                key={option.key ?? option.id}
                                                ref={(element) => {
                                                    optionRefs.current[idx] =
                                                        element;
                                                }}
                                                className={cn(
                                                    'flex cursor-pointer items-center gap-4 px-2 hover:bg-(--color-bg-subtle)',
                                                    isFirst && 'rounded-t-lg',
                                                    isHighlighted &&
                                                        'bg-(--color-bg-subtle)',
                                                )}
                                                onMouseEnter={() => {
                                                    setHighlightedOptionIndex(
                                                        idx,
                                                    );
                                                }}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    onOptionSelect(option);
                                                }}
                                            >
                                                <span className="flex-1">
                                                    {option.displayNode ??
                                                        option.displayValue}
                                                </span>
                                                {renderOption?.(option)}
                                            </div>
                                        );
                                    })}
                                    {fetchNextPage && (
                                        <div
                                            ref={
                                                observerTarget as React.RefObject<HTMLDivElement>
                                            }
                                            className="h-px w-full"
                                        />
                                    )}
                                </div>
                            )}

                            {/* Loading indicator for pagination */}
                            {isFetching && <SpinnerContainer />}

                            {!isPending &&
                                filteredOptions.length === 0 &&
                                searchTerm.length > 0 && (
                                    <div className="text-secondary p-4">
                                        {R.NoResultsFound}
                                    </div>
                                )}

                            {onRemove && (
                                <div
                                    className="cursor-pointer rounded-b-lg border border-r-0 border-b-0 border-l-0 border-(--color-border-muted) p-4 hover:bg-(--color-bg-subtle)"
                                    onMouseDown={onRemoveButtonClickHandler}
                                >
                                    <HorizontalContainer className="items-center">
                                        <FileRemoveIcon />
                                        {removeButtonText}
                                    </HorizontalContainer>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default SelectWithSearch;
